import type { RefObject } from 'react';
import type { FileManagerRef } from 'devextreme-react/file-manager';
import type FileSystemItem from 'devextreme/file_management/file_system_item';
import { apiFetch, readErrorMessage } from '../api/client';
import { showConfirmDialog } from '../dialogs/confirmDialog';
import { showToast } from '../notifications/toast';
import { buildUnzipUrl } from './fileBrowserApi';
import { conflictChoiceToPolicy, resolveFileConflict, type OnConflictPolicy } from './fileConflictDialog';
import { parseItemPathForRoutes, type MountRoute } from './createFileBrowserProvider';

export function defaultUnzipDestination(zipRelativePath: string): string {
  const slash = zipRelativePath.lastIndexOf('/');
  const fileName = slash < 0 ? zipRelativePath : zipRelativePath.slice(slash + 1);
  const parent = slash < 0 ? '' : zipRelativePath.slice(0, slash);
  const folderName = fileName.replace(/\.zip$/i, '');
  return parent ? `${parent}/${folderName}` : folderName;
}

export async function unzipArchive(
  mountId: string,
  request: { path: string; destinationPath?: string },
  onConflict?: OnConflictPolicy
): Promise<void> {
  const res = await apiFetch(buildUnzipUrl(mountId), {
    method: 'POST',
    body: JSON.stringify(onConflict ? { ...request, onConflict } : request)
  });

  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { errorText?: string; conflict?: boolean };
    if (body.conflict) {
      const choice = await resolveFileConflict(request.path.split('/').pop() ?? 'archive');
      const policy = conflictChoiceToPolicy(choice);
      if (choice === 'skip') return;
      if (choice === 'cancel' || !policy) throw new Error('Extract cancelled.');
      return unzipArchive(mountId, request, policy);
    }
  }

  if (!res.ok) throw new Error(await readErrorMessage(res));
}

export async function confirmAndUnzipArchive(
  item: FileSystemItem,
  routes: readonly MountRoute[],
  fileManagerRef: RefObject<FileManagerRef | null>
): Promise<boolean> {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative) return false;
  if (parsed.mount.mode !== 'ReadWrite') {
    showToast('This storage location is read-only.', 'warning', 3500);
    return false;
  }

  const destination = defaultUnzipDestination(parsed.relative);
  const confirmed = await showConfirmDialog({
    title: 'Extract zip archive',
    message: `Extract "${item.name}" into "${destination}"?`
  });
  if (!confirmed) return false;

  try {
    await unzipArchive(parsed.mount.id, {
      path: parsed.relative,
      destinationPath: destination
    });
    showToast('Archive extracted.', 'success', 3000);
    await fileManagerRef.current?.instance()?.refresh();
    return true;
  } catch (error) {
    showToast(error instanceof Error ? error.message : String(error), 'error', 5000);
    return false;
  }
}
